import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  X,
  Building2,
  MapPin,
  DollarSign,
  User,
  Phone,
  FileText,
  CreditCard,
} from "lucide-react";
import toast from "react-hot-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import AutocompleteInput from "./shared/AutocompleteInput";
import {
  COMPANY_CREATE_FORM_SCHEMA,
  COUNTRY_CODES,
  US_STATES,
} from "./constants";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

// Pre-compute filtered field arrays (computed once)
const LOCATION_FIELDS = COMPANY_CREATE_FORM_SCHEMA.LocationInformation.filter(
  (field) =>
    field.name !== "main_phone_country_code" &&
    field.name !== "main_phone_number"
);

const BILLING_FIELDS = COMPANY_CREATE_FORM_SCHEMA.BillingInformation.filter(
  (field) =>
    field.name !== "billing_phone_country_code" &&
    field.name !== "billing_phone_number"
);

// Zod schema for form validation
const companyFormSchema = z.object({
  // BasicCompanyInformation
  company_name: z.string().min(1, "Company Name is required").max(255),
  parent_company: z.string().max(255).optional().or(z.literal("")),
  company_formation_type: z.string().optional().or(z.literal("")),
  status: z.enum(["Qualified Lead", "Active Account", "DNC", "Archive"], {
    required_error: "Status is required",
  }),
  company_website: z.string().max(500).optional().or(z.literal("")),
  ownership_type: z.string().optional().or(z.literal("")),
  company_overview: z.string().optional().or(z.literal("")),
  company_description: z.string().optional().or(z.literal("")),

  // FinancialInformation
  standard_perm_fee_percentage: z
    .union([z.number(), z.string(), z.null()])
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = typeof val === "string" ? parseFloat(val) : val;
      return isNaN(num) ? null : num;
    })
    .nullable()
    .refine((val) => val === null || (val >= 0 && val <= 100), {
      message: "Standard Perm Fee Percentage must be between 0 and 100",
    }),
  industry: z.string().optional().or(z.literal("")),
  currency: z
    .enum(["USD", "CAD", "GBP", "PLN", "EUR", "AUD", "DKK", "AED", "INR"], {
      errorMap: () => ({ message: "Invalid currency" }),
    })
    .optional()
    .or(z.literal("")),

  // ManagementInformation
  account_manager: z.string().min(1, "Account Manager is required").max(255),

  // LocationInformation
  main_phone_country_code: z
    .string()
    .min(1, "Main Phone Country Code is required"),
  main_phone_number: z
    .string()
    .min(10, "Main Phone Number must be 10 digits")
    .max(10, "Main Phone Number must be 10 digits")
    .regex(/^\d+$/, "Main Phone Number must contain only digits"),
  address: z.string().min(1, "Address is required").max(500),
  address2: z.string().min(1, "Address 2 is required").max(500),
  city: z.string().min(1, "City is required").max(100),
  state: z.enum(US_STATES, {
    required_error: "State is required",
  }),
  zip_code: z.string().min(1, "Zip Code is required").max(20),
  country: z.string().min(1, "Country is required").max(100),

  // BillingInformation
  billing_contact: z.string().min(1, "Billing Contact is required").max(255),
  billing_frequency: z.string().optional().or(z.literal("")),
  billing_phone_country_code: z
    .string()
    .min(1, "Billing Phone Country Code is required"),
  billing_phone_number: z
    .string()
    .min(10, "Billing Phone Number must be 10 digits")
    .max(10, "Billing Phone Number must be 10 digits")
    .regex(/^\d+$/, "Billing Phone Number must contain only digits"),

  // InvoicingInformation
  invoice_frequency: z.enum(
    ["Weekly", "Bi-Weekly", "Bi-Monthly", "Batch", "Monthly"],
    {
      required_error: "Invoice Frequency is required",
    }
  ),
  batch_name: z.string().min(1, "Batch Name is required").max(255),
  invoice_format: z.enum(
    [
      "All Placements On One Invoice",
      "Each Placement on Separate Invoice",
      "Placement Grouped by Variable",
    ],
    {
      required_error: "Invoice Format is required",
    }
  ),
  net_terms: z
    .union([z.number(), z.string()])
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return 0;
      const num = typeof val === "string" ? parseFloat(val) : val;
      return isNaN(num) ? 0 : num;
    })
    .pipe(z.number().finite()),
});

// Initial form values
const defaultValues = {
  company_name: "",
  parent_company: "",
  company_formation_type: "",
  status: "",
  company_website: "",
  ownership_type: "",
  company_overview: "",
  company_description: "",
  standard_perm_fee_percentage: null,
  industry: "",
  currency: "USD",
  account_manager: "",
  main_phone_country_code: "",
  main_phone_number: "",
  address: "",
  address2: "",
  city: "",
  state: "",
  zip_code: "",
  country: "",
  billing_contact: "",
  billing_frequency: "",
  billing_phone_country_code: "",
  billing_phone_number: "",
  invoice_frequency: "",
  batch_name: "",
  invoice_format: "",
  net_terms: 0,
};

const CompanyCreateForm = ({ isOpen, onClose, onSuccess, company }) => {
  const isEditMode = !!company;

  const form = useForm({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      ...defaultValues,
      currency: "USD",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    trigger,
    formState: { isSubmitting, isValid },
  } = form;

  useEffect(() => {
    if (isOpen) {
      if (company) {
        // Populate form with company data for edit mode
        form.reset({
          company_name: company.company_name || "",
          parent_company: company.parent_company || "",
          company_formation_type: company.company_formation_type || "",
          status: company.status || "",
          company_website: company.company_website || "",
          ownership_type: company.ownership_type || "",
          company_overview: company.company_overview || "",
          company_description: company.company_description || "",
          standard_perm_fee_percentage:
            company.standard_perm_fee_percentage ?? null,
          industry: company.industry || "",
          currency: company.currency || "USD",
          account_manager: company.account_manager || "",
          main_phone_country_code: company.main_phone_country_code || "",
          main_phone_number: company.main_phone_number || "",
          address: company.address || "",
          address2: company.address2 || "",
          city: company.city || "",
          state: company.state || "",
          zip_code: company.zip_code || "",
          country: company.country || "",
          billing_contact: company.billing_contact || "",
          billing_frequency: company.billing_frequency || "",
          billing_phone_country_code: company.billing_phone_country_code || "",
          billing_phone_number: company.billing_phone_number || "",
          invoice_frequency: company.invoice_frequency || "",
          batch_name: company.batch_name || "",
          invoice_format: company.invoice_format || "",
          net_terms: company.net_terms ?? 0,
        });
      } else {
        // Reset to default values for new company
        form.reset({
          ...defaultValues,
          currency: "USD",
        });
      }
    } else {
      // Reset form when closed
      form.reset({
        ...defaultValues,
        currency: "USD",
      });
    }
  }, [isOpen, company, form]);

  const onSubmit = async (data) => {
    try {
      let response;

      if (isEditMode && company?.id) {
        // Update mode - use PUT with JSON
        const updateData = {
          company_name: data.company_name,
          parent_company: data.parent_company || null,
          company_formation_type: data.company_formation_type || null,
          status: data.status,
          company_website: data.company_website || null,
          ownership_type: data.ownership_type || null,
          company_overview: data.company_overview || null,
          company_description: data.company_description || null,
          standard_perm_fee_percentage: data.standard_perm_fee_percentage,
          industry: data.industry || null,
          currency: data.currency || null,
          account_manager: data.account_manager,
          main_phone_country_code: data.main_phone_country_code,
          main_phone_number: data.main_phone_number,
          address: data.address,
          address2: data.address2,
          city: data.city,
          state: data.state,
          zip_code: data.zip_code,
          country: data.country,
          billing_contact: data.billing_contact,
          billing_frequency: data.billing_frequency || null,
          billing_phone_country_code: data.billing_phone_country_code,
          billing_phone_number: data.billing_phone_number,
          invoice_frequency: data.invoice_frequency,
          batch_name: data.batch_name,
          invoice_format: data.invoice_format,
          net_terms: data.net_terms,
          created_by: "Test User",
        };

        response = await fetch(`${BACKEND_URL}/api/company/${company.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        });
      } else {
        // Create mode - use POST with FormData
        const formData = new FormData();
        formData.append("company_name", data.company_name);
        if (data.parent_company)
          formData.append("parent_company", data.parent_company);
        if (data.company_formation_type)
          formData.append(
            "company_formation_type",
            data.company_formation_type
          );
        formData.append("status", data.status);
        if (data.company_website)
          formData.append("company_website", data.company_website);
        if (data.ownership_type)
          formData.append("ownership_type", data.ownership_type);
        if (data.company_overview)
          formData.append("company_overview", data.company_overview);
        if (data.company_description)
          formData.append("company_description", data.company_description);
        if (data.standard_perm_fee_percentage !== null)
          formData.append(
            "standard_perm_fee_percentage",
            data.standard_perm_fee_percentage
          );
        if (data.industry) formData.append("industry", data.industry);
        if (data.currency) formData.append("currency", data.currency);
        formData.append("account_manager", data.account_manager);
        formData.append(
          "main_phone_country_code",
          data.main_phone_country_code
        );
        formData.append("main_phone_number", data.main_phone_number);
        formData.append("address", data.address);
        formData.append("address2", data.address2);
        formData.append("city", data.city);
        formData.append("state", data.state);
        formData.append("zip_code", data.zip_code);
        formData.append("country", data.country);
        formData.append("billing_contact", data.billing_contact);
        if (data.billing_frequency)
          formData.append("billing_frequency", data.billing_frequency);
        formData.append(
          "billing_phone_country_code",
          data.billing_phone_country_code
        );
        formData.append("billing_phone_number", data.billing_phone_number);
        formData.append("invoice_frequency", data.invoice_frequency);
        formData.append("batch_name", data.batch_name);
        formData.append("invoice_format", data.invoice_format);
        formData.append("net_terms", data.net_terms.toString());

        response = await fetch(`${BACKEND_URL}/api/company`, {
          method: "POST",
          body: formData,
        });
      }

      if (response.ok) {
        const result = await response.json();
        toast.success(
          `Company ${isEditMode ? "updated" : "created"} successfully!`
        );
        onSuccess && onSuccess(result);
        form.reset(defaultValues);
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));

        // Handle different error response formats
        let errorMessage = `Failed to ${
          isEditMode ? "update" : "create"
        } company`;

        if (typeof errorData.detail === "string") {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // FastAPI validation errors format
          errorMessage = errorData.detail
            .map((err) => `${err.loc?.join(" -> ") || "Field"}: ${err.msg}`)
            .join(", ");
        } else if (errorData.detail && typeof errorData.detail === "object") {
          // Object with error details
          errorMessage = JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }

        toast.error(errorMessage);
        // Don't close modal or reset form on error
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} company:`,
        error
      );
      toast.error(
        `Error ${
          isEditMode ? "updating" : "creating"
        } company. Please try again.`
      );
      // Don't close modal or reset form on error
    }
  };

  const renderField = (fieldConfig) => {
    const { name, label, field_type, isRequired, maxLength, options } =
      fieldConfig;

    // Special handling for country field - use AutocompleteInput
    if (name === "country") {
      return (
        <FormField
          key={name}
          control={form.control}
          name={name}
          render={({ field, fieldState }) => (
            <AutocompleteInput
              control={form.control}
              fieldName={name}
              displayFieldName={`${name}Display`}
              label={label}
              required={isRequired}
              placeholder="Search country..."
              searchEndpoint="/api/company/country-codes"
              valueKey="id"
              displayKey="country"
              queryParamName="search"
              responseTransformer={(data) => {
                // Extract unique countries from country_codes response
                const countrySet = new Set();
                const countryList = [];

                (data.country_codes || []).forEach((item) => {
                  if (item.country && !countrySet.has(item.country)) {
                    countrySet.add(item.country);
                    countryList.push({
                      id: item.country, // Use country name as id
                      country: item.country, // Display value
                    });
                  }
                });

                return countryList.sort((a, b) =>
                  a.country.localeCompare(b.country)
                );
              }}
              field={field}
              fieldState={fieldState}
              setValue={form.setValue}
            />
          )}
        />
      );
    }

    // Special handling for account_manager field - use AutocompleteInput
    if (name === "account_manager") {
      return (
        <FormField
          key={name}
          control={form.control}
          name={name}
          render={({ field, fieldState }) => {
            // Custom setValue that stores the full name instead of ID
            const customSetValue = (fieldName, value, options) => {
              const displayFieldName = `${name}Display`;

              if (fieldName === displayFieldName) {
                // When displayFieldName is set (full name), also update account_manager
                form.setValue(name, value, options);
                form.setValue(displayFieldName, value, options);
              } else if (fieldName === name) {
                // When account_manager is set directly, just set it
                form.setValue(fieldName, value, options);
              } else {
                // For other fields, use default behavior
                form.setValue(fieldName, value, options);
              }
            };

            return (
              <AutocompleteInput
                control={form.control}
                fieldName={name}
                displayFieldName={`${name}Display`}
                label={label}
                required={isRequired}
                placeholder="Search account manager..."
                searchEndpoint="/api/users/search"
                valueKey="id"
                displayFormatter={(user) => {
                  // Combine firstName and lastName for display
                  const firstName = user.firstName || "";
                  const lastName = user.lastName || "";
                  return `${firstName} ${lastName}`.trim() || user.email || "";
                }}
                field={field}
                fieldState={fieldState}
                setValue={customSetValue}
              />
            );
          }}
        />
      );
    }

    switch (field_type) {
      case "text":
        return (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field, fieldState }) => (
              <FormItem>
                <Label>
                  {label}{" "}
                  {isRequired && <span className="text-red-500">*</span>}
                </Label>
                <FormControl>
                  <Input
                    {...field}
                    maxLength={maxLength}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    className={
                      fieldState.error
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case "select":
        return (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field, fieldState }) => (
              <FormItem>
                <Label>
                  {label}{" "}
                  {isRequired && <span className="text-red-500">*</span>}
                </Label>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    trigger(name); // Trigger validation for this field
                  }}
                  value={
                    field.value && field.value !== "" ? field.value : undefined
                  }
                >
                  <FormControl>
                    <SelectTrigger
                      className={
                        fieldState.error
                          ? "border-red-500 focus:ring-red-500"
                          : ""
                      }
                    >
                      <SelectValue
                        placeholder={`Select ${label.toLowerCase()}`}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent side="bottom" align="start" sideOffset={4}>
                    {options?.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case "textarea":
        return (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field, fieldState }) => (
              <FormItem className="md:col-span-2">
                <Label>
                  {label}{" "}
                  {isRequired && <span className="text-red-500">*</span>}
                </Label>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={3}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    className={
                      fieldState.error
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case "number":
        return (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field, fieldState }) => (
              <FormItem>
                <Label>
                  {label}{" "}
                  {isRequired && <span className="text-red-500">*</span>}
                </Label>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    value={field.value === null ? "" : field.value}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === "" ? null : parseFloat(value));
                    }}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    step="any"
                    min={
                      name === "standard_perm_fee_percentage" ? 0 : undefined
                    }
                    max={
                      name === "standard_perm_fee_percentage" ? 100 : undefined
                    }
                    className={
                      fieldState.error
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {isEditMode ? "Edit Company" : "Add Company"}
          </h2>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="text-white hover:text-gray-200 hover:bg-transparent"
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        {/* Form */}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="overflow-y-auto"
            style={{ maxHeight: "calc(90vh - 140px)" }}
          >
            <div className="p-6 space-y-6">
              {/* Basic Company Information Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Basic Company Information
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {COMPANY_CREATE_FORM_SCHEMA.BasicCompanyInformation.map(
                    (field) => renderField(field)
                  )}
                </div>
              </div>

              {/* Financial Information Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Financial Information
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {COMPANY_CREATE_FORM_SCHEMA.FinancialInformation.map(
                    (field) => renderField(field)
                  )}
                </div>
              </div>

              {/* Management Information Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <User className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Management Information
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {COMPANY_CREATE_FORM_SCHEMA.ManagementInformation.map(
                    (field) => renderField(field)
                  )}
                </div>
              </div>

              {/* Location Information Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Location Information
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Main Phone with Country Code */}
                  <div className="space-y-2">
                    <Label>
                      Main Phone <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <FormField
                          control={form.control}
                          name="main_phone_country_code"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={
                                  field.value && field.value !== ""
                                    ? field.value
                                    : undefined
                                }
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={
                                      fieldState.error
                                        ? "border-red-500 focus:ring-red-500"
                                        : ""
                                    }
                                  >
                                    <SelectValue placeholder="Select country code" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent
                                  side="bottom"
                                  align="start"
                                  sideOffset={4}
                                >
                                  {COUNTRY_CODES.map(({ code, country }) => (
                                    <SelectItem key={code} value={code}>
                                      {code} ({country})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-8">
                        <FormField
                          control={form.control}
                          name="main_phone_number"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="tel"
                                  maxLength={10}
                                  minLength={10}
                                  inputMode="numeric"
                                  placeholder="Phone number (10 digits)"
                                  className={
                                    fieldState.error
                                      ? "border-red-500 focus-visible:ring-red-500"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  {LOCATION_FIELDS.map((field) => renderField(field))}
                </div>
              </div>

              {/* Billing Information Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Billing Information
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Billing Phone with Country Code */}
                  <div className="">
                    <Label>
                      Billing Phone <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <FormField
                          control={form.control}
                          name="billing_phone_country_code"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={
                                  field.value && field.value !== ""
                                    ? field.value
                                    : undefined
                                }
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={
                                      fieldState.error
                                        ? "border-red-500 focus:ring-red-500"
                                        : ""
                                    }
                                  >
                                    <SelectValue placeholder="Select country code" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent
                                  side="bottom"
                                  align="start"
                                  sideOffset={4}
                                >
                                  {COUNTRY_CODES.map(({ code, country }) => (
                                    <SelectItem key={code} value={code}>
                                      {code} ({country})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-8">
                        <FormField
                          control={form.control}
                          name="billing_phone_number"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="tel"
                                  maxLength={10}
                                  minLength={10}
                                  inputMode="numeric"
                                  placeholder="Phone number (10 digits)"
                                  className={
                                    fieldState.error
                                      ? "border-red-500 focus-visible:ring-red-500"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  {BILLING_FIELDS.map((field) => renderField(field))}
                </div>
              </div>

              {/* Invoicing Information Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Invoicing Information
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {COMPANY_CREATE_FORM_SCHEMA.InvoicingInformation.map(
                    (field) => renderField(field)
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !isValid}
                className={
                  !isSubmitting && isValid
                    ? "bg-purple-600 hover:bg-purple-700"
                    : "bg-gray-400 cursor-not-allowed"
                }
              >
                {isSubmitting
                  ? isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : isEditMode
                  ? "Update Company"
                  : "Create Company"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default CompanyCreateForm;
